// Take the hook from the data module, NOT the UI barrel: that door re-exports
// `client` (-> scribeHTTP -> authModel), so a VALUE import from it pulls that chain
// into this hook's module graph and, combined with a test's `requireActual`, loops
// authModel before init ('Cannot access auth before initialization').
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useWorkspaceId } from '@heidi/workspace/data';
import { useIngestComputerUseNotePushCandidatesCore } from '../../../data/projects/hooks/useIngestComputerUseNotePushCandidates';
import { getWorkHostPort } from '../../work-home/work-home-ports';

/**
 * Headless: feed note-push tool results off the project timeline into the
 * note-push candidate store, bound to the app's store writes, the workspace the
 * store keys under, and the stored-placements GET's invalidation.
 */
export const useIngestComputerUseNotePushCandidates = (
  args: Parameters<typeof useIngestComputerUseNotePushCandidatesCore>[0]
) => {
  const desktop = getWorkHostPort('desktop');
  const ingestHints = desktop.useSetIngestNotePushHints();
  const ingestRecorded = desktop.useSetIngestNotePushRecorded();
  const ingestFieldCandidates = desktop.useSetIngestFieldCandidates();
  const clearCandidates = desktop.useSetClearCandidateMappings();
  // Workspace the record was stored under — scopes the placement-store GET key.
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const invalidateTargets = useCallback(
    (templateId: string) => {
      void queryClient.invalidateQueries({
        queryKey: [desktop.notePushTargetsQueryKey, workspaceId, templateId],
      });
    },
    [desktop, queryClient, workspaceId]
  );

  useIngestComputerUseNotePushCandidatesCore(args, {
    workspaceId,
    ingestHints,
    ingestRecorded,
    ingestFieldCandidates,
    clearCandidates: () => {
      clearCandidates(undefined);
    },
    invalidateTargets,
  });
};
